/**
 * Column model: definition, order, width, visibility, persistence.
 *
 * Two kinds of column exist and they behave differently on purpose.
 *
 * **Built-ins** are derived from the track payload. They can be reordered,
 * resized and hidden, but never deleted — a curator who hides Title can always
 * bring it back, whereas deleting it would need a "restore defaults" escape
 * hatch nobody would find.
 *
 * **Custom columns** are defined by the curator and stored in the database, keyed
 * by audio payload hash so a curator's own data survives a file move or re-tag.
 * They are the only kind that can be deleted, and deleting one destroys data, so
 * that path confirms.
 *
 * Layout (order/width/visibility) is a per-viewer convenience and lives in
 * localStorage. The *definitions* of custom columns and their values are shared
 * library data and live in the database. Keeping that split straight matters: a
 * curator moving to another machine should find their columns, not their pixel
 * widths.
 */

export type ColumnKind = "builtin" | "custom";
export type ColumnAlign = "left" | "right";

export interface ColumnDef {
  id: string;
  label: string;
  width: number;
  minWidth: number;
  align: ColumnAlign;
  kind: ColumnKind;
  /** Custom columns carry their declared type so the cell renders correctly. */
  valueType?: "text" | "number" | "boolean" | "date";
  /** Hidden from the grid but still available in the column picker. */
  hidden?: boolean;
  /** Built-ins that would be meaningless to hide. */
  locked?: boolean;
}

export const BUILTIN_COLUMNS: ColumnDef[] = [
  { id: "index", label: "#", width: 40, minWidth: 32, align: "right", kind: "builtin", locked: true },
  { id: "title", label: "Title", width: 240, minWidth: 120, align: "left", kind: "builtin", locked: true },
  { id: "artist", label: "Artist", width: 170, minWidth: 80, align: "left", kind: "builtin" },
  { id: "album", label: "Album", width: 170, minWidth: 80, align: "left", kind: "builtin" },
  { id: "year", label: "Year", width: 60, minWidth: 48, align: "right", kind: "builtin" },
  { id: "genre", label: "Genre", width: 160, minWidth: 90, align: "left", kind: "builtin" },
  { id: "bpm", label: "BPM", width: 74, minWidth: 56, align: "right", kind: "builtin" },
  { id: "key", label: "Key", width: 72, minWidth: 60, align: "left", kind: "builtin" },
  // 1-5, a curator's level outranking the computed one, drawn as bars.
  { id: "level", label: "Energy", width: 76, minWidth: 64, align: "left", kind: "builtin" },
  { id: "duration", label: "Time", width: 64, minWidth: 52, align: "right", kind: "builtin" },
  // Off by default in a player; one click away in Columns.
  { id: "ready", label: "Cues", width: 96, minWidth: 52, align: "left", kind: "builtin", hidden: true },
  { id: "comment", label: "Comment", width: 170, minWidth: 80, align: "left", kind: "builtin", hidden: true },
  { id: "energy", label: "Energy (0-1)", width: 90, minWidth: 56, align: "right", kind: "builtin", hidden: true },
  { id: "dance", label: "Dance", width: 66, minWidth: 52, align: "right", kind: "builtin", hidden: true },
  { id: "lufs", label: "LUFS", width: 74, minWidth: 56, align: "right", kind: "builtin", hidden: true },
  { id: "peak", label: "Peak", width: 66, minWidth: 52, align: "right", kind: "builtin", hidden: true },
  { id: "cue", label: "Cue in/out", width: 100, minWidth: 70, align: "right", kind: "builtin", hidden: true },
  { id: "path", label: "Path", width: 220, minWidth: 90, align: "left", kind: "builtin", hidden: true },
  { id: "needsedit", label: "Needs edit", width: 110, minWidth: 70, align: "left", kind: "builtin", hidden: true },
  // Hand-picked only, or a release date: whether Auto DJ and Flow may pick it.
  { id: "autopick", label: "Auto picks", width: 130, minWidth: 80, align: "left", kind: "builtin", hidden: true },
  // The row's ⋯ menu. Always last, never hidden.
  { id: "more", label: "", width: 40, minWidth: 40, align: "right", kind: "builtin", locked: true },
];

/** Built-in columns whose search field has a different name. */
const SORT_FIELD: Record<string, string> = {
  level: "energylevel",
  dance: "danceability",
  peak: "truepeak",
  cue: "cuein",
};
/** Columns with nothing to sort by. */
const UNSORTABLE = new Set(["index", "more", "ready", "autopick"]);

/**
 * The engine field a column header sorts by (`/api/tracks?sort=`), or null.
 * Every built-in with a value sorts, and so does every custom column, including
 * ones added later: `custom:<key>:<type>`.
 */
export function sortField(col: ColumnDef): string | null {
  if (UNSORTABLE.has(col.id)) return null;
  if (col.kind === "custom") {
    const type = col.valueType === "number" ? "number" : col.valueType === "boolean" ? "bool" : "text";
    return `custom:${col.id}:${type}`;
  }
  return SORT_FIELD[col.id] ?? col.id;
}

const STORAGE_KEY = "nova.columns.v2";

/** Per-viewer layout: which columns, in what order, how wide. */
interface StoredLayout {
  order: string[];
  widths: Record<string, number>;
  hidden: string[];
}

function readLayout(): StoredLayout | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredLayout) : null;
  } catch {
    // Blocked storage or corrupt JSON: fall back to defaults rather than break.
    return null;
  }
}

export function saveLayout(columns: ColumnDef[]): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        order: columns.map((c) => c.id),
        widths: Object.fromEntries(columns.map((c) => [c.id, c.width])),
        hidden: columns.filter((c) => c.hidden).map((c) => c.id),
      } satisfies StoredLayout)
    );
  } catch {
    // Layout simply will not persist; the session still works.
  }
}

/**
 * Merge stored layout over the current set of available columns.
 *
 * Deliberately driven by `available`, not by what was stored: a column removed
 * from the app (or a custom column deleted by someone else) must disappear, and a
 * newly added one must show up rather than being invisible because an old layout
 * did not mention it.
 */
export function applyLayout(available: ColumnDef[]): ColumnDef[] {
  const stored = readLayout();
  if (!stored) return pinLast(available);

  const rank = new Map(stored.order.map((id, i) => [id, i]));
  const hidden = new Set(stored.hidden);

  return pinLast([...available]
    .map((col) => ({
      ...col,
      width: stored.widths[col.id] ?? col.width,
      // A locked column can never be hidden, whatever an old layout claims.
      hidden: col.locked ? false : hidden.has(col.id),
    }))
    .sort((a, b) => {
      const ra = rank.get(a.id);
      const rb = rank.get(b.id);
      // Columns the stored order does not know about sort to the end, in their
      // declared order, rather than jumping to the front.
      if (ra === undefined && rb === undefined) return 0;
      if (ra === undefined) return 1;
      if (rb === undefined) return -1;
      return ra - rb;
    }));
}

/** The ⋯ column stays at the right edge whatever order was stored. */
function pinLast(columns: ColumnDef[]): ColumnDef[] {
  const more = columns.filter((c) => c.id === "more");
  return [...columns.filter((c) => c.id !== "more"), ...more];
}

export function moveColumn(columns: ColumnDef[], fromId: string, toId: string): ColumnDef[] {
  if (fromId === toId || fromId === "more" || toId === "more") return columns;
  const next = [...columns];
  const from = next.findIndex((c) => c.id === fromId);
  const to = next.findIndex((c) => c.id === toId);
  if (from < 0 || to < 0) return columns;
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

export function resizeColumn(columns: ColumnDef[], id: string, width: number): ColumnDef[] {
  return columns.map((c) =>
    c.id === id ? { ...c, width: Math.max(c.minWidth, Math.round(width)) } : c
  );
}

export function toggleColumn(columns: ColumnDef[], id: string): ColumnDef[] {
  return columns.map((c) =>
    c.id === id && !c.locked ? { ...c, hidden: !c.hidden } : c
  );
}

export function resetLayout(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do; the caller reloads from defaults regardless.
  }
}
